import React, { lazy, Suspense } from 'react';
import { store } from './store/store';
import { Provider } from 'react-redux';
import Spinner from './components/Spinner';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { RouteEnums } from '@Helper/Enums';
import DependenciesImo from './pages/Dependencies/DependenciesImo';
import PersonalTemplates from '@Pages/PersonalTemplates/PersonalTemplates';
import UserRegistered from './pages/authentication/UserRegistered';
import '@fontsource/quicksand';

const AcceptDealspaceInvitiation = lazy(
  () => import('./pages/authentication/AcceptDealspaceInvitiation')
);
const AcceptPlayBookInvitiation = lazy(
  () => import('./pages/authentication/AcceptPlayBookInvitiation')
);
const ChooseDealSpace = lazy(
  () => import('./pages/authentication/ChooseDealSpace')
);
const ChooseExperience = lazy(
  () => import('./pages/authentication/ChooseExperience')
);
const ConnectPlaybook = lazy(
  () => import('./pages/authentication/ConnectPlaybook')
);
const CreateDealspace = lazy(
  () => import('./pages/authentication/CreateDealspace')
);
const EmailVerification = lazy(
  () => import('./pages/authentication/EmailVerification')
);
const ForgotPassword = lazy(
  () => import('./pages/authentication/ForgotPassword')
);
const Login = lazy(() => import('./pages/authentication/Login'));
const ResetPassword = lazy(
  () => import('./pages/authentication/ResetPassword')
);
const SignUp = lazy(() => import('./pages/authentication/SignUp'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const DealspaceDirectory = lazy(
  () => import('./pages/DealspaceDirectory/DealspaceDirectory')
);
const Decisions = lazy(() => import('./pages/Decisions/Decisions'));
const Dependencies = lazy(() => import('./pages/Dependencies/Dependencies'));
const ImoKnowledge = lazy(() => import('./pages/ImoKnowledge/ImoKnowledge'));
const Issues = lazy(() => import('./pages/Issues/Issues'));
const Milestone = lazy(() => import('./pages/Milestone/Milestone'));
const MyTasks = lazy(() => import('./pages/MyTasks/MyTasks'));
const Notification = lazy(() => import('./pages/Notification/Notification'));
const Playbook = lazy(() => import('./pages/Playbook'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const Risk = lazy(() => import('./pages/Risk/Risk'));
const RouteRequiresLogin = lazy(() => import('./RouteRequiresLogin'));
const EmailConfirmation = lazy(
  () => import('./pages/authentication/EmailConfirmation')
);

const PageNotFound = lazy(() => import('./pages/ErrorPages/PageNotFound'));
const LinkBroken = lazy(() => import('./pages/ErrorPages/LinkBroken'));
const UnderConstruction = lazy(
  () => import('./pages/ErrorPages/UnderConstruction')
);
const ServerError = lazy(() => import('./pages/ErrorPages/ServerError'));
const NoInternet = lazy(() => import('./pages/ErrorPages/NoInternet'));
const PermissionIssue = lazy(
  () => import('./pages/ErrorPages/PermissionIssue')
);
const DependenciesOut = lazy(
  () => import('./pages/Dependencies/DependenciesOut')
);

/* Payment  */

const PricingAndPlans = lazy(
  () => import('./pages/Payment/Pages/PricingAndPlans')
);

const Subscription = lazy(() => import('./pages/Payment/Pages/Subscription'));

const Invoices = lazy(() => import('./pages/Payment/Pages/PaymentDetails'));
const BillingInfo = lazy(() => import('./pages/Payment/Pages/BillingInfo'));
const PaymentMethods = lazy(
  () => import('./pages/Payment/Pages/PaymentMethods')
);
const PaymentPlan = lazy(() => import('./pages/Payment/Pages/PaymentPlan'));
const ResultMessage = lazy(() => import('./pages/Payment/Pages/ResultMessage'));
const WorkstreamPlan = lazy(
  () => import('./pages/Payment/Pages/WorkstreamPlan')
);
const PersonalTemplatesDetail = lazy(
  () => import('./pages/PersonalTemplatesDetail')
);

function App() {
  return (
    <Provider store={store}>
      <Suspense fallback={<Spinner />}>
        <BrowserRouter>
          <Routes>
            <Route
              path={RouteEnums.Dashboard + ':id'}
              element={
                <RouteRequiresLogin>
                  <Dashboard />
                </RouteRequiresLogin>
              }
            />
            <Route path={RouteEnums.Login} element={<Login />} />
            <Route
              path={RouteEnums.EmailVerification}
              element={<EmailVerification />}
            />
            <Route
              path={RouteEnums.EmailConfirmation}
              element={<EmailConfirmation />}
            />
            <Route path={RouteEnums.SignUp} element={<SignUp />} />
            <Route
              path={RouteEnums.ChooseDealSpace}
              element={<ChooseDealSpace />}
            />
            <Route
              path={RouteEnums.Dependencies + ':id'}
              element={<DependenciesImo />}
            />
            <Route
              path={RouteEnums.ForgotPassword}
              element={<ForgotPassword />}
            />{' '}
            <Route
              path={RouteEnums.UserRegistered}
              element={<UserRegistered />}
            />
            <Route
              path={RouteEnums.ResetPassword}
              element={<ResetPassword />}
            />
            <Route
              path={RouteEnums.ConnectPlaybook}
              element={<ConnectPlaybook />}
            />
            <Route
              path={RouteEnums.Notifications + ':id'}
              element={<Notification />}
            />
            <Route
              path={RouteEnums.AcceptPlayBookInvitiation}
              element={<AcceptPlayBookInvitiation />}
            />
            <Route
              path={RouteEnums.Subscription + ':id'}
              element={<Subscription />}
            />
            <Route
              path={RouteEnums.AcceptDealspaceInvitiation}
              element={<AcceptDealspaceInvitiation />}
            />
            <Route
              path={RouteEnums.Playbook + ':id/:pid/:tabNumber'}
              element={
                <RouteRequiresLogin>
                  <Playbook />
                </RouteRequiresLogin>
              }
            />
            <Route
              path={RouteEnums.PersonalTemplates}
              element={
                <RouteRequiresLogin>
                  <PersonalTemplates />
                </RouteRequiresLogin>
              }
            />
            <Route
              path={RouteEnums.PersonalTemplatesDetail + ':id'}
              element={
                <RouteRequiresLogin>
                  <PersonalTemplatesDetail />
                </RouteRequiresLogin>
              }
            />
            <Route
              path={RouteEnums.CreateDealspace}
              element={<CreateDealspace />}
            />
            <Route
              path={RouteEnums.ChooseExperience}
              element={<ChooseExperience />}
            />
            <Route path={RouteEnums.Reports + ':id'} element={<Reports />} />
            <Route
              path={RouteEnums.DealspaceDirectory + ':id'}
              element={<DealspaceDirectory />}
            />
            <Route path="" element={<Login />} />
            <Route
              path={RouteEnums.Milestone + ':id'}
              element={<Milestone />}
            />
            <Route path={RouteEnums.MyTasks + ':id'} element={<MyTasks />} />
            <Route path={RouteEnums.MyRisks + ':id'} element={<Risk />} />
            <Route path={RouteEnums.MyIssues + ':id'} element={<Issues />} />
            <Route
              path={RouteEnums.MyDecisions + ':id'}
              element={<Decisions />}
            />
            <Route
              path={RouteEnums.DependenciesIn + ':id'}
              element={<Dependencies />}
            />
            <Route
              path={RouteEnums.DependenciesOut + ':id'}
              element={<DependenciesOut />}
            />
            <Route
              path={RouteEnums.ImoKnowledge + ':id'}
              element={<ImoKnowledge />}
            />
            <Route
              path={RouteEnums.ImoTools + ':id'}
              element={<ImoKnowledge />}
            />
            <Route path={RouteEnums.Settings + ':id'} element={<Milestone />} />
            {/* Error Pages */}
            <Route path="*" element={<PageNotFound />}></Route>
            <Route path="/link-broken" element={<LinkBroken />}></Route>
            <Route path="/under" element={<UnderConstruction />}></Route>
            <Route path="/server-error" element={<ServerError />}></Route>
            <Route path="/no-internet" element={<NoInternet />}></Route>
            <Route path="/permission" element={<PermissionIssue />}></Route>
            <Route
              path={RouteEnums.PricingAndPlans}
              element={<PricingAndPlans />}
            />
            <Route path={RouteEnums.PaymentDetails} element={<Invoices />} />
            <Route path={RouteEnums.BillingInfo} element={<BillingInfo />} />
            <Route
              path={RouteEnums.PaymentMethod}
              element={<PaymentMethods />}
            />
            <Route path={RouteEnums.PaymentPlan} element={<PaymentPlan />} />
            <Route
              path={RouteEnums.ResultMessage}
              element={<ResultMessage />}
            />
            <Route
              path={RouteEnums.WorkstreamPlan}
              element={<WorkstreamPlan />}
            />
          </Routes>
        </BrowserRouter>
      </Suspense>
    </Provider>
  );
}

export default App;
